/**
 * Colapsa notificaciones que llegan en ráfaga (p.ej. mismo evento en EN + ES):
 * si dos entradas tienen marca temporal dentro de windowMs, se fusionan en una (mensajes EN/ES combinados).
 */

export const DEFAULT_NOTIFICATION_BURST_MS = 1000;

export function normalizeNotificationTimestamp(n) {
  if (!n || typeof n !== 'object') return Date.now();
  const t = n.timestamp;
  if (typeof t === 'number' && Number.isFinite(t)) return t;
  if (typeof t === 'string') {
    const ms = Date.parse(t);
    if (Number.isFinite(ms)) return ms;
  }
  return Date.now();
}

/** Une dos payloads conservando ambos idiomas cuando procede */
export function mergeNotificationEntries(prev, next) {
  const a = prev && typeof prev === 'object' ? prev : {};
  const b = next && typeof next === 'object' ? next : {};
  const out = { ...a, ...b };
  const msgA = a.message && typeof a.message === 'object' ? a.message : {};
  const msgB = b.message && typeof b.message === 'object' ? b.message : {};
  out.message_en = b.message_en ?? a.message_en ?? msgB.message_en ?? msgA.message_en;
  out.message_es = b.message_es ?? a.message_es ?? msgB.message_es ?? msgA.message_es;
  if (typeof b.message === 'object' && b.message !== null) {
    out.message = { ...msgA, ...msgB, ...b.message };
  } else if (typeof a.message === 'object' && a.message !== null) {
    out.message = { ...msgA };
  }
  const tPrev = normalizeNotificationTimestamp(a);
  const tNext = normalizeNotificationTimestamp(b);
  out.timestamp = tNext >= tPrev ? (b.timestamp ?? a.timestamp) : (a.timestamp ?? b.timestamp);
  return out;
}

/**
 * Ordena por tiempo ascendente y fusiona entradas consecutivas si Δt < windowMs.
 * @param {object[]} notifs
 * @param {number} [windowMs]
 * @returns {object[]}
 */
export function collapseNotificationBursts(notifs, windowMs = DEFAULT_NOTIFICATION_BURST_MS) {
  if (!Array.isArray(notifs) || notifs.length <= 1) return notifs || [];
  const sorted = [...notifs].sort((x, y) => normalizeNotificationTimestamp(x) - normalizeNotificationTimestamp(y));
  const out = [];
  for (const item of sorted) {
    if (out.length === 0) {
      out.push(item);
      continue;
    }
    const last = out[out.length - 1];
    const dt = normalizeNotificationTimestamp(item) - normalizeNotificationTimestamp(last);
    if (dt >= 0 && dt < windowMs) {
      out[out.length - 1] = mergeNotificationEntries(last, item);
    } else {
      out.push(item);
    }
  }
  return out;
}

/**
 * Texto a mostrar solo en el idioma de sesión (sin mezclar EN/ES ni otros idiomas).
 * Usa message_<lang> en raíz y, si existe, dentro de `message` como objeto (respuestas common-core).
 * Al cambiar `lang` en el banner, basta con re-render: el mismo objeto sigue teniendo todos los message_*.
 */
export function pickNotificationMessageForLang(notif, langRaw) {
  if (!notif || typeof notif !== 'object') return '';
  const lang = String(langRaw || 'en').trim();
  const short = lang.includes('-') ? lang.split('-')[0] : lang;
  const keys = [`message_${lang}`, `message_${short}`];
  for (const kk of keys) {
    const v = notif[kk];
    if (v != null && String(v).trim().length) return String(v);
  }
  const nested = notif.message;
  if (nested && typeof nested === 'object') {
    for (const kk of keys) {
      const v = nested[kk];
      if (v != null && String(v).trim().length) return String(v);
    }
    const flat = nested.message;
    if (flat != null && String(flat).trim().length) return String(flat);
  }
  if (typeof nested === 'string' && nested.trim().length) return nested;
  return '';
}
